import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

/**
 * Interface representing an item in the shopping cart
 */
interface CartItem {
  /** Name of the item */
  name: string;
  /** Price of the item */
  price: number;
  /** Quantity of the item */
  quantity: number;
}

/**
 * Interface defining the cart context functionality
 */
interface CartContextType {
  /** Array of items in the cart */
  items: CartItem[];
  /** Function to add an item to the cart */
  addItem: (item: CartItem) => void;
  /** Function to remove an item from the cart */
  removeItem: (index: number) => void;
  /** Function to update item quantity (removes item when quantity <= 0) */
  updateQuantity: (index: number, quantity: number) => void;
  /** Set quantity for a product by name (adds if missing and quantity > 0) */
  setQuantityByName: (
    name: string,
    price: number,
    quantity: number,
  ) => void;
  /** Current cart quantity for a product name (0 if absent) */
  getQuantityByName: (name: string) => number;
  /** Function to clear all items from the cart */
  clearCart: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

/**
 * Provider component that manages shopping cart state.
 * Handles cart operations and persists cart data in localStorage.
 *
 * @param props - The provider props
 * @returns A context provider for cart functionality
 */
export const CartProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [items, setItems] = useState<CartItem[]>(() => {
    const savedItems = localStorage.getItem("cartItems");
    return savedItems ? JSON.parse(savedItems) : [];
  });

  useEffect(() => {
    localStorage.setItem("cartItems", JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((newItem: CartItem) => {
    if (newItem.quantity <= 0) return;

    setItems((prevItems) => {
      const existingItemIndex = prevItems.findIndex(
        (item) => item.name === newItem.name,
      );

      if (existingItemIndex >= 0) {
        const updatedItems = [...prevItems];
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity:
            updatedItems[existingItemIndex].quantity + newItem.quantity,
        };
        return updatedItems;
      }

      return [...prevItems, newItem];
    });
  }, []);

  const removeItem = useCallback((index: number) => {
    setItems((prevItems) => prevItems.filter((_, i) => i !== index));
  }, []);

  const updateQuantity = useCallback((index: number, quantity: number) => {
    setItems((prevItems) => {
      if (quantity <= 0) {
        return prevItems.filter((_, i) => i !== index);
      }
      const updatedItems = [...prevItems];
      updatedItems[index] = { ...updatedItems[index], quantity };
      return updatedItems;
    });
  }, []);

  const setQuantityByName = useCallback(
    (name: string, price: number, quantity: number) => {
      setItems((prevItems) => {
        const existingItemIndex = prevItems.findIndex(
          (item) => item.name === name,
        );

        if (quantity <= 0) {
          if (existingItemIndex < 0) return prevItems;
          return prevItems.filter((_, i) => i !== existingItemIndex);
        }

        if (existingItemIndex >= 0) {
          const updatedItems = [...prevItems];
          updatedItems[existingItemIndex] = {
            ...updatedItems[existingItemIndex],
            quantity,
            price,
          };
          return updatedItems;
        }

        return [...prevItems, { name, price, quantity }];
      });
    },
    [],
  );

  const getQuantityByName = useCallback(
    (name: string) => {
      const item = items.find((entry) => entry.name === name);
      return item?.quantity ?? 0;
    },
    [items],
  );

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        updateQuantity,
        setQuantityByName,
        getQuantityByName,
        clearCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
